import { FieldArray } from 'formik';
import { FormikProvider } from 'formik';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { queryCache } from 'react-query';
import { Poppins, Spacer, TableSkeleton } from 'src/common';
import { Scope, SortState, VariableEdit } from './types';
import _ from 'lodash';
import {
  categorizeVariables,
  checkIsDirty,
  genEmptyVariable,
  sortVariables,
  transformVariablesToGqlInput,
  validationSchema,
} from './util';
import { useFormik } from 'formik';
import useScenarioNames from './useMappedNames';
import CheckBox from 'src/components/form/CheckBox';
import { motion } from 'framer-motion';
import { Head, Row } from './comps';
import Button from 'src/components/form/Button';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { Div } from './styled';
import useAssessmentVariables from './useAssessmentVariables';
import { useAuth } from 'src/state/auth';
import { useCache } from 'src/graphql';
import VariableCSVReader from 'src/components/VariableCSVReader';
import {
  CsvImportPreview,
  CsvVariable,
  CsvImportGroups,
  CsvWorkspaceGroups,
} from 'src/components/VariableCSVReader/types';
import { useUpsertAssessmentVariablesMutation, useUpsertWorkspaceVariablesMutation } from 'src/graphql/_generated';

/**
 * Filters CSV variables based on group actions (include/exclude)
 * Returns only variables from groups with action='include'
 */
const filterCsvVariablesByGroups = (
  variables: CsvVariable[],
  groups: CsvImportGroups | undefined,
  workspaceGroups: CsvWorkspaceGroups | undefined,
): CsvVariable[] => {
  const excludedNames = new Set<string>();

  if (groups) {
    Object.values(groups).forEach((group) => {
      if (group.action === 'exclude') {
        group.variables.forEach((v: CsvVariable) => excludedNames.add(v.name));
      }
    });
  }

  if (workspaceGroups) {
    Object.values(workspaceGroups).forEach((group) => {
      if (group.action === 'exclude') {
        group.variables.forEach((v: CsvVariable) => excludedNames.add(v.name));
      }
    });
  }

  return variables.filter((v) => !excludedNames.has(v.name));
};

interface WorkspaceVariablesFormProps {
  onClose: () => void;
  onCloseRequest?: () => void;
  scope: Scope;
  onDirtyChange?: (isDirty: boolean) => void;
  readOnly?: boolean;
  isModal?: boolean;
  hideEmptyVariablesControlled?: boolean;
}

/**
 * Applies CSV variables to existing form variables
 * Only applies variables that have actually changed
 */
const applyCsvToVariables = (params: {
  currentVariables: VariableEdit[];
  csvVariables: CsvVariable[];
  wVars: VariableEdit[];
  scope: Scope;
}): VariableEdit[] => {
  const { currentVariables, csvVariables, wVars, scope } = params;
  const newVariables = _.cloneDeep(currentVariables);

  console.group('[CSV Apply] Applying CSV variables');
  console.log('Current variables:', currentVariables);
  console.log('CSV variables:', csvVariables);
  console.log('Workspace variables:', wVars);

  csvVariables.forEach((csvVar) => {
    const existingVarIndex = newVariables.findIndex((v) => v.name === csvVar.name);
    const existsInWorkspaceVars = wVars?.some((wv) => wv.name === csvVar.name);

    console.log(`[${csvVar.name}]`, {
      existingVarIndex,
      existsInWorkspaceVars,
    });

    if (existingVarIndex !== -1) {
      const existingVar = newVariables[existingVarIndex];

      // Check if values actually changed
      const isValueSame = existingVar.value === csvVar.value;
      const isCurrencySame = existingVar.currency === csvVar.currency;

      console.log(`  Existing var:`, existingVar);
      console.log(`  CSV var:`, csvVar);
      console.log(`  Value comparison: existing=${existingVar.value}, csv=${csvVar.value}, same=${isValueSame}`);
      console.log(
        `  Currency comparison: existing=${existingVar.currency}, csv=${csvVar.currency}, same=${isCurrencySame}`,
      );

      if (isValueSame && isCurrencySame) {
        console.log(`  → SKIP (no changes)`);
        return; // Skip - no changes needed
      }

      if (scope.type === 'assessment' && existsInWorkspaceVars) {
        console.log(`  → UPDATE as override`);
        newVariables[existingVarIndex] = {
          ...existingVar,
          value: csvVar.value || 0,
          currency: csvVar.currency,
          workspace_variable: false,
          isGlobal: false,
          isInUse: [],
          _isNewOverwrite: true,
          _isFromCSV: true,
        };
      } else {
        console.log(`  → UPDATE values`);
        newVariables[existingVarIndex] = {
          ...existingVar,
          value: csvVar.value || 0,
          currency: csvVar.currency,
        };
      }
    } else if (scope.type === 'assessment' && existsInWorkspaceVars) {
      const workspaceVar = wVars?.find((wv) => wv.name === csvVar.name);
      if (workspaceVar) {
        // Check if values actually changed from workspace var
        const isValueSame = workspaceVar.value === csvVar.value;
        const isCurrencySame = workspaceVar.currency === csvVar.currency;

        console.log(`  Workspace var:`, workspaceVar);
        console.log(`  Value comparison: workspace=${workspaceVar.value}, csv=${csvVar.value}, same=${isValueSame}`);
        console.log(
          `  Currency comparison: workspace=${workspaceVar.currency}, csv=${csvVar.currency}, same=${isCurrencySame}`,
        );

        if (isValueSame && isCurrencySame) {
          console.log(`  → SKIP (same as workspace var)`);
          return; // Skip - same as workspace variable
        }

        console.log(`  → CREATE as override`);
        const newVar: VariableEdit = {
          ...workspaceVar,
          value: csvVar.value || 0,
          currency: csvVar.currency,
          workspace_variable: false,
          isGlobal: false,
          isInUse: [],
          _isNewOverwrite: true,
          _isFromCSV: true,
        };
        newVariables.push(newVar);
      }
    } else {
      // Create new variable
      console.log(`  → CREATE new variable`);
      const newVar: VariableEdit = {
        id: Date.now() + Math.random(),
        name: csvVar.name,
        value: csvVar.value || 0,
        currency: csvVar.currency,
        workspace_variable: scope.type === 'workspace',
        isGlobal: false,
        isInUse: [],
        alias: null,
        _isNew: true,
        _isFromCSV: true,
      };
      newVariables.push(newVar);
    }
  });

  console.log('Result variables:', newVariables);
  console.groupEnd();

  return newVariables;
};

const WorkspaceVariablesForm: React.FC<WorkspaceVariablesFormProps> = ({
  onClose,
  onCloseRequest,
  scope,
  onDirtyChange,
  isModal,
  hideEmptyVariablesControlled = true,
}) => {
  const [initialVariables, setInitialVariables] = useState<VariableEdit[]>([]);
  const [pendingCsvImport, setPendingCsvImport] = useState<CsvImportPreview | null>(null);
  const [error, setError] = useState('');
  const [variablesRepo, setVariablesRepo] = useState<VariableEdit[]>([]);
  const [showAssessmentVarOnly, setShowAssessmentVarOnly] = useState(false);
  const [hideEmptyVariables, setHideEmptyVariables] = useState(hideEmptyVariablesControlled);
  const [sort, setSort] = useState<SortState>({ by: 'name', isAsc: true });
  const { variables: wVars, profile } = useAuth();
  const { assessmentVariables, isAssessmentVariablesLoading } = useAssessmentVariables({
    assessmentId: scope.type === 'assessment' ? scope.id : undefined,
  });

  const { scenarioNames, assessmentNames } = useScenarioNames({
    variables: variablesRepo,
  });
  const { refetchQuery } = useCache();

  const [upsertWorkspaceVariables] = useUpsertWorkspaceVariablesMutation();
  const [upsertAssessmentVariables] = useUpsertAssessmentVariablesMutation();

  useEffect(() => {
    setHideEmptyVariables(hideEmptyVariablesControlled);
  }, [hideEmptyVariablesControlled]);

  useLayoutEffect(() => {
    if (!isAssessmentVariablesLoading) {
      const assessmentVariablesInit = assessmentVariables || [];
      const repo = [...wVars, ...assessmentVariablesInit];

      setVariablesRepo(repo);

      let vars: VariableEdit[];
      if (scope.type === 'workspace') {
        vars = wVars;
      } else {
        const wVarIds = new Set(wVars.map((wv) => wv.id));
        const cleanAssessmentVars = assessmentVariablesInit.filter((av) => !wVarIds.has(av.id));
        const uniqueAssessmentVars = Array.from(
          cleanAssessmentVars.reduce((map, v) => map.set(v.name, v), new Map<string, VariableEdit>()).values(),
        );
        const assessmentVarNames = uniqueAssessmentVars.map((v) => v.name);
        const wVarsWithoutOverrides = wVars.filter((wv) => !assessmentVarNames.includes(wv.name));
        vars = [...wVarsWithoutOverrides, ...uniqueAssessmentVars];
      }

      if (vars.length) {
        setInitialVariables(vars);
      } else {
        setInitialVariables([genEmptyVariable(scope.type)]);
      }
    }
  }, [isAssessmentVariablesLoading, scope, showAssessmentVarOnly, hideEmptyVariables, wVars, assessmentVariables]);

  const formik = useFormik({
    initialValues: {
      variables: initialVariables,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      // Apply pending CSV import before saving
      // Filter out excluded groups first
      const filteredCsvVariables = pendingCsvImport
        ? filterCsvVariablesByGroups(
            pendingCsvImport.variables,
            pendingCsvImport.groups,
            pendingCsvImport.workspaceGroups,
          )
        : [];

      const variablesToSave =
        pendingCsvImport && filteredCsvVariables.length > 0
          ? applyCsvToVariables({
              currentVariables: values.variables,
              csvVariables: filteredCsvVariables,
              wVars,
              scope,
            })
          : values.variables;

      const promises: Promise<unknown>[] = [];

      if (!showAssessmentVarOnly) {
        const isWorkspaceScope = scope.type === 'workspace';
        const excludeDeleteNames = isWorkspaceScope
          ? []
          : variablesToSave.filter((el) => !el.workspace_variable && !el.isGlobal).map((el) => el.name);

        const wvInput = isWorkspaceScope
          ? variablesToSave
          : variablesToSave.filter((el) => el.workspace_variable || el.isGlobal);
        const wvOriginal = isWorkspaceScope
          ? initialVariables
          : initialVariables.filter((el) => el.workspace_variable || el.isGlobal);
        const categorized = categorizeVariables(wvInput, wvOriginal, excludeDeleteNames, 'workspace');

        if (categorized.length) {
          promises.push(
            upsertWorkspaceVariables({
              variables: {
                workspaceId: profile?.activeWorkspace?.id!,
                variables: transformVariablesToGqlInput(categorized),
              },
            }),
          );
        }
      }

      if (scope.type === 'assessment') {
        const assessmentInput = variablesToSave.filter((el) => {
          if (el._isReverted) return false;
          if (el.workspace_variable || el.isGlobal) return false;

          const existsInAssessmentVars = assessmentVariables?.some((av) => av.id === el.id);
          const isNewOverride = !!el._isNewOverwrite;
          const isNewPureAssessmentVar = !!el._isNew && !wVars?.some((wv) => wv.name === el.name);
          return existsInAssessmentVars || isNewOverride || isNewPureAssessmentVar;
        });
        const assessmentOriginal = initialVariables.filter((el) => {
          if (el.workspace_variable || el.isGlobal) return false;
          return assessmentVariables?.some((av) => av.id === el.id) || el._isNewOverwrite || el._isNew;
        });
        const categorized = categorizeVariables(assessmentInput, assessmentOriginal, [], 'assessment');

        if (categorized.length) {
          promises.push(
            upsertAssessmentVariables({
              variables: {
                assessmentId: scope.id,
                variables: transformVariablesToGqlInput(categorized),
              },
            }),
          );
        }
      }

      return Promise.all(promises)
        .then(async () => {
          queryCache.invalidateQueries();
          await refetchQuery({ include: ['profile'] });
          setPendingCsvImport(null);
          onClose();
        })
        .catch((err: Error) => setError(err.message || 'Something went wrong'));
    },
  });

  const { values, handleSubmit, isValid, isSubmitting, setFieldValue, resetForm } = formik;

  const initialVariablesRef = useRef<string>('');
  useEffect(() => {
    const serialized = JSON.stringify(initialVariables.map((v) => ({ id: v.id, name: v.name })));
    if (initialVariablesRef.current && initialVariablesRef.current !== serialized) {
      resetForm({ values: { variables: initialVariables } });
    }
    initialVariablesRef.current = serialized;
  }, [initialVariables, resetForm]);

  useEffect(() => {
    const sorted = sortVariables(values.variables, sort);
    if (sorted) {
      setFieldValue('variables', sorted);
    }
  }, [sort]);

  const onOverride = (name: string, override: boolean) => {
    const original = wVars.find((el) => el.name === name);
    if (!override) {
      const newVariables = _.cloneDeep(values.variables);
      const idx = newVariables.findIndex((el) => el.name === name);

      if (original && idx !== -1) {
        newVariables[idx] = { ..._.cloneDeep(original), _isReverted: true };
        setFieldValue(`variables`, newVariables);
      }
    } else {
      const newVariables = _.cloneDeep(values.variables);
      const idx = newVariables.findIndex((el) => el.name === name);

      if (original && idx !== -1) {
        const targetVariable = _.cloneDeep(original);
        newVariables[idx] = {
          ...targetVariable,
          workspace_variable: false,
          isGlobal: false,
          isInUse: [],
          _isNewOverwrite: true,
        };

        setFieldValue(`variables`, newVariables);
      }
    }
  };

  // Check if form is dirty (includes form changes OR pending CSV import)
  const isFormDirty = checkIsDirty(values.variables, initialVariables) || !!pendingCsvImport;

  useEffect(() => {
    onDirtyChange?.(isFormDirty);
  }, [isFormDirty, onDirtyChange]);

  // Get workspace variable names for the CSV reader
  const workspaceVariableNames = wVars.map((v) => v.name);

  return (
    <Div>
      <div className="h-padding">
        <div className="vw-head">
          {isModal ? (
            <Poppins className="m-title" px={28}>
              {scope.type === 'assessment' ? 'Assessment' : 'Workspace'} Variables
            </Poppins>
          ) : (
            <div />
          )}
          {isModal && (
            <div className="vw-head__checks">
              {scope.type === 'assessment' && (
                <div
                  className="vw-head__avo-check"
                  onClick={() => setShowAssessmentVarOnly(!showAssessmentVarOnly)}
                  data-cy="show-assessment-variables-only"
                >
                  <Poppins color="cflowerBlue" px={14} weight={500}>
                    Show assessment variables only
                  </Poppins>
                  <CheckBox small isChecked={showAssessmentVarOnly} />
                </div>
              )}
              <div
                className="vw-head__avo-check"
                onClick={() => setHideEmptyVariables(!hideEmptyVariables)}
                data-cy="hide-empty-variables"
              >
                <Poppins color="cflowerBlue" px={14} weight={500}>
                  Hide empty variables
                </Poppins>
                <CheckBox small isChecked={hideEmptyVariables} />
              </div>
            </div>
          )}
        </div>
      </div>
      <Spacer $px={20} />
      <div className="divider" />
      <Spacer $px={24} />

      {!isAssessmentVariablesLoading ? (
        <FormikProvider value={formik}>
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.1 }}
            onSubmit={handleSubmit}
          >
            <div className="h-padding">
              <FieldArray name="variables">
                {({ remove, push }) => {
                  return (
                    <>
                      <div className="rows-grid">
                        <Head sort={sort} setSort={setSort} />
                        {values.variables.map((el, idx) => {
                          const hasWorkspaceVar = wVars?.some((wv) => wv.name === el.name);
                          const isVWar = el.workspace_variable || el.isGlobal || hasWorkspaceVar;
                          const isOverride =
                            scope.type === 'assessment' &&
                            hasWorkspaceVar &&
                            !el._isReverted &&
                            (assessmentVariables?.some((av) => av.name === el.name) || !!el._isNewOverwrite);
                          const canOverride = scope.type === 'assessment' && hasWorkspaceVar;
                          const shouldHideForAssessmentOnly = showAssessmentVarOnly && hasWorkspaceVar && !isOverride;
                          const isHidden = (hideEmptyVariables && el.value === null) || shouldHideForAssessmentOnly;
                          const typePrefix = el.workspace_variable ? 'w' : el.isGlobal ? 'g' : 'a';
                          const rowKey = `${typePrefix}-${el.id}`;

                          return (
                            <Row
                              key={rowKey}
                              idx={idx}
                              onRemove={() => {
                                if (isOverride && !el._isNew) {
                                  onOverride(el.name, false);
                                } else {
                                  remove(idx);
                                }
                              }}
                              scenarioNames={scenarioNames}
                              assessmentNames={assessmentNames}
                              scope={scope}
                              isVWar={isVWar}
                              canOverride={!!canOverride}
                              isOverride={!!isOverride}
                              onOverride={() => onOverride(el.name, !isOverride)}
                              isHidden={isHidden}
                              isGlobal={el.isGlobal}
                              existsInWorkspaceVars={!!hasWorkspaceVar}
                            />
                          );
                        })}
                      </div>
                      <Spacer $px={15} />
                      <VariableCSVReader
                        originalVariables={scope.type === 'assessment' ? assessmentVariables || [] : wVars}
                        workspaceVariableNames={workspaceVariableNames}
                        scope={scope}
                        pendingImport={pendingCsvImport}
                        onPendingImportChange={setPendingCsvImport}
                        onAddNewVariable={() => {
                          push(genEmptyVariable(scope.type));
                          mpEvent(MPEvents.ButtonClick, {
                            button: 'Add new variable',
                            modal: isModal ? 'Variables modal' : undefined,
                            tags: ['VARIABLES'],
                          });
                        }}
                      />
                      <Spacer $px={35} />
                      <div className="btn-container">
                        {isModal && (
                          <Button secondary disabled={isSubmitting} onClick={() => (onCloseRequest || onClose)()}>
                            CANCEL
                          </Button>
                        )}
                        <Button
                          type="submit"
                          primary
                          disabled={!isValid || !isFormDirty || isSubmitting}
                          data-cy="save-variables-btn"
                          onClick={() =>
                            mpEvent(MPEvents.ButtonClick, {
                              button: 'Save',
                              modal: isModal ? 'Variables modal' : undefined,
                              tags: ['VARIABLES'],
                            })
                          }
                        >
                          SAVE
                        </Button>
                      </div>
                    </>
                  );
                }}
              </FieldArray>
              {error && (
                <>
                  <Spacer $px={23} />
                  <div className="error">{error}</div>
                  <Spacer $px={23} />
                </>
              )}
            </div>
          </motion.form>
        </FormikProvider>
      ) : (
        <div className="h-padding">
          <TableSkeleton />
          <Spacer $px={54} />
        </div>
      )}
    </Div>
  );
};

export default WorkspaceVariablesForm;
